/**
 * Fix actions for an invalid design (PLAN §3 and §6: "Continue is replaced by the fix action while
 * the design is invalid, never a dead button"), and the envelope check Check runs before a size is
 * confirmed (FND-01, FND-02).
 *
 * The first validation error decides the fix, in this order:
 *  1. the family's own fix (`error.fix = { label, patch }`, from src/families), applied as one undoable edit;
 *  2. a size outside the construction's envelope: "Set height to 2200 mm";
 *  3. the grid family's openings remainder: "Add 12 mm to the top opening";
 *  4. "Undo the last change" when the last edit is what broke the design, else "Go back to the
 *     last valid design" when there was one, else "Show the problem" (scrolls to and focuses it).
 */
import type { Design, Issue } from '@/domain';
import { fmtMm, setDimension } from '@/domain';
import { familyOf } from '@/families/registry';
import { applyFix } from '@/families/fix';
import { announce, toast } from './announce';
import { commitDim, limitsFor, placeRemainderTop, shapeFix } from './edits';
import { MATERIAL_CHOICES, resolveStock, stockById } from '@/families/stock';
import { app, onStoreEvent, ui } from './state';

/** A validation error as the families programme may send it (fix optional). */
export type IssueWithFix = Issue & { fix?: { label: string; patch: Record<string, unknown> } };

export type DimKey = 'W' | 'H' | 'D';
const DIMS: readonly DimKey[] = ['W', 'H', 'D'];
const WORDS: Record<DimKey, { name: string; over: string; under: string }> = {
  W: { name: 'width', over: 'wider', under: 'narrower' },
  H: { name: 'height', over: 'taller', under: 'shorter' },
  D: { name: 'depth', over: 'deeper', under: 'shallower' },
};

export interface EnvelopeIssue {
  key: DimKey;
  value: number;
  min: number;
  max: number;
  /** The nearest size the construction allows (ticks). */
  nearest: number;
  /** "2370 mm is taller than this construction allows (600–2200 mm)" */
  message: string;
  /** "Set height to 2200 mm" */
  fixLabel: string;
}

/** Is the design's W, H or D outside the construction profile's limits? */
export function envelopeIssue(key: DimKey, design: Design = app.store.design): EnvelopeIssue | null {
  const v = design[key];
  const lim = limitsFor(key);
  if (!Number.isFinite(v) || (v >= lim.min && v <= lim.max)) return null;
  const over = v > lim.max;
  const nearest = over ? lim.max : lim.min;
  return {
    key, value: v, min: lim.min, max: lim.max, nearest,
    message: `${fmtMm(v)} mm is ${over ? WORDS[key].over : WORDS[key].under} than this construction allows (${fmtMm(lim.min)}–${fmtMm(lim.max)} mm)`,
    fixLabel: `Set ${WORDS[key].name} to ${fmtMm(nearest)} mm`,
  };
}
export const envelopeIssues = (design: Design = app.store.design): EnvelopeIssue[] => DIMS.map((k) => envelopeIssue(k, design)).filter((x): x is EnvelopeIssue => !!x);

/** Bring one size inside the envelope (an undoable edit, announced). */
export function useNearest(key: DimKey): boolean {
  const e = envelopeIssue(key);
  if (!e) return true;
  if (commitDim(key, fmtMm(e.nearest))) return true;
  // the size alone would introduce another error: apply it anyway so the next fix can follow
  app.store.apply((d) => setDimension(d, key, e.nearest, app.store.stock), { label: `${WORDS[key].name[0]!.toUpperCase()}${WORDS[key].name.slice(1)} ${fmtMm(e.nearest)} mm` });
  delete ui.fieldErrors[key];
  announce(`${WORDS[key].name[0]!.toUpperCase()}${WORDS[key].name.slice(1)} set to ${fmtMm(e.nearest)} mm.`);
  return true;
}

// ---------------------------------------------------------------------------------------------
// Tracking what broke the design (for "Undo the last change") and the last valid design
// ---------------------------------------------------------------------------------------------
let lastValid: Design | null = null;
let breaker: string | null = null;
let prevOk = false;
let owner: unknown = null;
onStoreEvent((s, e) => {
  if (owner !== app.store) { owner = app.store; lastValid = null; breaker = null; prevOk = false; ui.stockBreak = null; } // a new project: nothing known yet
  if (ui.stockBreak && (s.build.validation.ok || s.design.stockProfileId !== ui.stockBreak.to)) ui.stockBreak = null;
  if (e.type === 'commit' || e.type === 'export' || e.type === 'view') return;
  const ok = s.build.validation.ok;
  if (e.type === 'reset' || e.type === 'restore') {
    lastValid = ok ? s.design : null;
    breaker = null;
  } else {
    breaker = !ok && prevOk && e.type === 'apply' ? e.label : null;
    if (ok) lastValid = s.design;
  }
  prevOk = ok;
});

/**
 * Apply a family-supplied fix (an error's or an advice's) as one undoable edit. The patch rules
 * live in src/families/fix.ts: top-level design keys directly (on a grid, also keys the design
 * does not have yet, such as `bays` from a span fix), the rest through the family's setParam.
 */
export function applyIssueFix(fix: { label: string; patch: Record<string, unknown> }): void {
  app.store.apply((d) => applyFix(d, fix), { label: fix.label });
  announce(`${fix.label}.${app.store.build.validation.ok ? ' The design is valid again.' : ''}`);
}
const applyPatch = (patch: Record<string, unknown>, label: string): void => applyIssueFix({ label, patch });

/** Scroll to and focus the control the error names, or the error list when there is none. */
export function showProblem(e: Issue | undefined = app.store.build.validation.errors[0]): void {
  const f = e?.field ?? '';
  const sel = [
    DIMS.includes(f as DimKey) ? `#dim-${f}` : '',
    `#param-${f}`, `[data-param="${f}"] input, [data-param="${f}"] button`,
    /opening-(\d+)$/.test(f) ? `#op-${/opening-(\d+)$/.exec(f)![1]}` : '',
    '#shapeErrors', '#sumRule', '#panelBody .field-err',
  ].filter(Boolean);
  const el = sel.map((s) => { try { return document.querySelector<HTMLElement>(s); } catch { return null; } }).find((x) => !!x);
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  if (!el.matches('input, button, select, textarea, [tabindex]')) el.setAttribute('tabindex', '-1');
  el.focus({ preventScroll: true });
  el.classList.remove('is-nudge');
  void el.offsetWidth;
  el.classList.add('is-nudge');
  announce(e?.message ?? '');
}

export interface DesignFix {
  kind: 'family' | 'envelope' | 'remainder' | 'undo' | 'last-valid' | 'show';
  label: string;
  /** Why the design is invalid (shown beside the action). */
  reason: string;
  act: () => void;
}

/** The fix for the first validation error, or null when the design is valid. */
export function designFix(): DesignFix | null {
  const b = app.store.build;
  if (b.validation.ok) return null;
  const e = b.validation.errors[0] as IssueWithFix | undefined;
  const reason = e?.message ?? 'The design is not valid.';
  const env = envelopeIssues();
  if (e?.fix?.label) {
    const fx = e.fix;
    const dim = env.find((x) => x.key === e.field);
    return { kind: 'family', label: fx.label, reason: dim ? `${dim.message}.` : reason, act: () => applyPatch(fx.patch ?? {}, fx.label) };
  }
  const envFirst = env.find((x) => x.key === e?.field) ?? env[0];
  if (envFirst) return { kind: 'envelope', label: envFirst.fixLabel, reason: `${envFirst.message}.`, act: () => { useNearest(envFirst.key); } };
  const rem = shapeFix();
  if (rem) return { kind: 'remainder', label: rem.label, reason: rem.reason || reason, act: () => placeRemainderTop(rem.bay) };
  if (owner === app.store && breaker && app.store.canUndo && app.store.undoLabel === breaker) {
    const l = breaker;
    return { kind: 'undo', label: 'Undo the last change', reason, act: () => { app.store.undo(); announce(`Undid ${l}.`); } };
  }
  if (owner === app.store && lastValid && lastValid !== app.store.design && familyOf(lastValid).definition.id === familyOf(app.store.design).definition.id) {
    const d = lastValid;
    return { kind: 'last-valid', label: 'Go back to the last valid design', reason, act: () => { app.store.apply(() => d, { label: 'Back to the last valid design' }); toast('Back to the last valid design. Undo brings your edits back.'); } };
  }
  return { kind: 'show', label: 'Show the problem', reason, act: () => showProblem(e) };
}

// ---------------------------------------------------------------------------------------------
// A material that broke the design (run 2, p09/p11): said in Look at once, fixed in Look or Pack
// ---------------------------------------------------------------------------------------------
const thicknessOf = (id: string): number => { const st = stockById(id); return st ? resolveStock(st).thicknessTicks : 0; };
/** "Birch plywood", "Sheesham", or "Sheesham 45 mm" for a thicker board of a timber. */
export function materialLabel(id: string): string {
  const base = MATERIAL_CHOICES.find((m) => m.stockId === id);
  if (base) return base.label;
  const thick = MATERIAL_CHOICES.find((m) => m.thicker?.some((t) => t.stockId === id));
  const t = thick?.thicker?.find((x) => x.stockId === id);
  return thick && t ? `${thick.label} ${t.label}` : id;
}
export interface MaterialBreak { from: string; to: string; fromLabel: string; message: string }
/** The material change that made the design invalid, while it still does; null otherwise. */
export function materialBreak(): MaterialBreak | null {
  const sb = ui.stockBreak;
  const b = app.store.build;
  if (!sb || b.validation.ok || app.store.design.stockProfileId !== sb.to) return null;
  const e = b.validation.errors[0];
  const thicker = thicknessOf(sb.to) > thicknessOf(sb.from);
  const cells = e ? /(\d+(?:\.\d+)?) mm high; the lowest is (\d+(?:\.\d+)?) mm/.exec(e.message) : null;
  const message = cells
    ? `${thicker ? 'Thicker' : 'Thinner'} boards make the cells ${cells[1]} mm, under the ${cells[2]} mm minimum.`
    : `${materialLabel(sb.to)} ${thicker ? '(thicker boards)' : ''}: ${e?.message ?? 'the design is no longer valid.'}`.replace(' :', ':');
  return { from: sb.from, to: sb.to, fromLabel: materialLabel(sb.from), message };
}
/** A fix that changes the design (not undo, not "show the problem"), for Look and Pack. */
export function realFix(): DesignFix | null {
  const f = designFix();
  return f && (f.kind === 'family' || f.kind === 'envelope' || f.kind === 'remainder') ? f : null;
}
